/**
 * Public MCP surface for the rest of the app.
 *
 * Consumers today:
 *   - `piChat.createChatAgent()` calls `collectMcpAgentTools()` at agent
 *     build time to fold any connected servers' tools into the
 *     tool list pi-ai sees.
 *   - `ChatModelPicker` calls `startConnect` / `clearConnection` /
 *     `listConnections` / `onConnectionsChanged` for the UI.
 *   - Sidebar dim loaders (e.g. Linear projects/tickets) call
 *     `callMcpToolDirect` to invoke a known tool by name without the
 *     `tools/list` discovery round-trip.
 *
 * Everything else (OAuth, transport, schema translation) is internal.
 * If we later swap the local browser-side connector for a backend MCP
 * gateway, only this module's transport-pool + `collectMcpAgentTools`
 * impl needs to change — the chat code is decoupled from the transport.
 *
 * ── Transport pool ─────────────────────────────────────────────
 *
 * `initializeMcp` is a 2-RPC handshake (`initialize` + `notifications/
 * initialized`) on a fresh `Mcp-Session-Id`. Each `tools/list` adds a
 * third round-trip before any useful work happens. We pool the
 * post-handshake transport per `(tenantId, userId, serverId)` so the second
 * caller (chat re-build, sidebar label flush) reuses the existing
 * session instead of redoing the handshake.
 *
 * Pool invalidation triggers:
 *   - Registry signals a connection has been removed
 *     (`onConnectionsChanged` + storage event from another tab).
 *   - The handshake itself failed — the rejected entry evicts itself
 *     so a future caller can retry under a fresh entry.
 *   - A pooled call surfaces a session-expired signal from the server
 *     (see `isSessionExpiredError`). The call retries exactly once
 *     under a freshly-handshaken transport — see `withSessionRetry`.
 *
 * The session-expiry retry is the load-bearing piece for long-lived
 * clients. Before pooling, every `createChatAgent()` minted a fresh
 * session so stale ones were churned out automatically; with pooling,
 * a stale `Mcp-Session-Id` would otherwise stick around until the user
 * disconnected. All call paths go through `withSessionRetry` so this
 * recovery is transparent — callers see a brief extra round-trip on
 * the rare expiry, not an error.
 */

import {
  listConnections,
  loadConnection,
  getValidAccessToken,
  resolveEndpoints,
  recordHandshakeError,
  clearHandshakeError,
  connectionStatus,
  shortError,
  onConnectionsChanged,
  SERVER_REGISTRY,
} from "./registry.js";
import { initializeMcp, listMcpTools, callMcpTool } from "./client.js";
import { mcpToolToAgentTool } from "./agentTools.js";
import {
  browserStorageOwnerIsCurrent,
  requireBrowserStorageOwner,
} from "../browserStorageScope.js";
import { onAuthIdentityChange } from "../authTokenStore.js";

export {
  SERVER_REGISTRY,
  listServerDefinitions,
  listConnections,
  loadConnection,
  clearConnection,
  startConnect,
  recordTokens,
  saveApiKeyConnection,
  saveServerConfig,
  shortError,
  isApiKeyServer,
  isConfigServer,
  connectionStatus,
  resolveGleanConfig,
  onConnectionsChanged,
  onConnectionStatusChanged,
} from "./registry.js";

// key="tenantId:userId:serverId" -> one captured-owner transport entry.
const _pool = new Map();

function poolKey(owner, serverId) {
  return `${owner.key}:${serverId}`;
}

// Get (or build) a pooled, post-handshake transport for this connection.
// First caller drives the handshake; concurrent + subsequent callers
// await the same promise. Throws if the server isn't registered or
// the handshake fails — eviction on failure means a retry is allowed
// without manual cleanup.
export async function getMcpTransport(tenantId, serverId, capturedOwner = null) {
  if (!tenantId) throw new Error("getMcpTransport: tenantId required");
  if (!serverId) throw new Error("getMcpTransport: serverId required");
  const def = SERVER_REGISTRY[serverId];
  if (!def) throw new Error(`Unknown MCP server: ${serverId}`);

  const owner = capturedOwner || requireBrowserStorageOwner(tenantId);
  if (!browserStorageOwnerIsCurrent(owner)) {
    throw new Error("Origin identity changed before MCP transport use.");
  }
  const key = poolKey(owner, serverId);
  const existing = _pool.get(key);
  if (existing) return existing.ready;

  // Resolve from the stored record so apiKey servers with templated
  // URLs and config servers (Glean) pick up whatever the user just
  // saved. Plain OAuth servers resolve to their static def.mcpUrl.
  // Checked before anything is pooled: an unconfigured server must not
  // leave a rejected entry behind.
  const baseUrl = resolveEndpoints(def, loadConnection(tenantId, serverId))?.mcpUrl;
  if (!baseUrl) {
    throw new Error(`${def.name} is not configured yet. Finish connecting it under Model & settings, MCP tab.`);
  }

  const entry = { ready: null, owner, tenantId, serverId, transport: null, baseUrl };
  entry.ready = (async () => {
    const getToken = () => {
      if (!browserStorageOwnerIsCurrent(owner)) {
        throw new Error("Origin identity changed before MCP authentication.");
      }
      return getValidAccessToken(tenantId, serverId);
    };
    const onUnauthorized = getToken;
    try {
      const info = await initializeMcp({ baseUrl, getToken, onUnauthorized });
      if (!browserStorageOwnerIsCurrent(owner) || _pool.get(key) !== entry) {
        try { info.transport?.close?.(); } catch { /* optional transport close */ }
        throw new Error("Origin identity changed during MCP transport initialization.");
      }
      entry.transport = info.transport;
      clearHandshakeError(tenantId, serverId);
      return info;
    } catch (err) {
      // Evict on handshake failure so a future caller can retry under
      // a fresh entry instead of re-awaiting a permanently rejected promise.
      if (_pool.get(key) === entry) _pool.delete(key);
      // Remember the failure on the record so the picker shows the row
      // as failed with the reason. Skipped when identity moved on: the
      // record belongs to whoever is signed in now.
      if (browserStorageOwnerIsCurrent(owner)) {
        recordHandshakeError(tenantId, serverId, describeHandshakeError(err));
      }
      throw err;
    }
  })();
  _pool.set(key, entry);
  return entry.ready;
}

// Drop a pooled transport. Pass no `serverId` to drop everything for a
// tenant. Safe to call when nothing is pooled.
export function evictMcpTransport(tenantId, serverId) {
  if (!tenantId) return;
  let owner;
  try { owner = requireBrowserStorageOwner(tenantId); } catch { return; }
  if (serverId) {
    evictEntry(poolKey(owner, serverId));
    return;
  }
  for (const [key, entry] of [..._pool.entries()]) {
    if (entry.owner.key === owner.key) evictEntry(key);
  }
}

function evictEntry(key) {
  const entry = _pool.get(key);
  if (!entry) return;
  _pool.delete(key);
  try { entry.transport?.close?.(); } catch { /* optional transport close */ }
}

function evictAllMcpTransports() {
  for (const key of [..._pool.keys()]) evictEntry(key);
}

onAuthIdentityChange(evictAllMcpTransports);

// Sync the pool with the registry: when a connection is cleared (or
// disappears from another tab via the storage event), evict the
// matching pooled transport so the next caller re-handshakes against
// whatever connection state replaces it.
onConnectionsChanged(() => {
  for (const [key, entry] of [..._pool.entries()]) {
    if (!browserStorageOwnerIsCurrent(entry.owner)) {
      evictEntry(key);
      continue;
    }
    const conns = listConnections(entry.tenantId);
    if (!conns.some((c) => c.id === entry.serverId)) {
      evictEntry(key);
      continue;
    }
    // Same server, different endpoint (Edit URL, re-saved api key): the
    // pooled session belongs to the old host.
    const def = SERVER_REGISTRY[entry.serverId];
    const current = resolveEndpoints(def, loadConnection(entry.tenantId, entry.serverId))?.mcpUrl ?? null;
    if (entry.baseUrl && current !== entry.baseUrl) evictEntry(key);
  }
});

// Detect a server-side session-expired error. MCP spec says servers
// SHOULD return HTTP 404 when they no longer recognize the
// `Mcp-Session-Id` we're sending. Some servers also signal via a
// JSON-RPC error whose message names the session. Both paths get
// retried under a freshly-handshaken transport exactly once.
//
// Intentionally conservative: a 404 with no prior successful call is
// indistinguishable from a permanently-broken endpoint, but the worst
// case is one wasted re-handshake before the same error propagates.
function isSessionExpiredError(err) {
  const msg = String(err?.message || "");
  if (/MCP HTTP 404\b/.test(msg)) return true;
  if (/session/i.test(msg) && /(not found|expired|invalid|unknown|missing|terminated)/i.test(msg)) return true;
  return false;
}

// Run `fn(transport)` against the pooled transport; on a session-
// expired error, evict + re-handshake and retry exactly once. All
// pooled call paths (tools/list, tools/call, anything we add later)
// route through here so recovery is uniform.
async function withSessionRetry(tenantId, serverId, fn, capturedOwner = null) {
  const owner = capturedOwner || requireBrowserStorageOwner(tenantId);
  let info = await getMcpTransport(tenantId, serverId, owner);
  if (!browserStorageOwnerIsCurrent(owner)) {
    throw new Error("Origin identity changed before MCP call.");
  }
  try {
    return await fn(info.transport);
  } catch (err) {
    if (!isSessionExpiredError(err)) throw err;
    evictEntry(poolKey(owner, serverId));
    if (!browserStorageOwnerIsCurrent(owner)) {
      throw new Error("Origin identity changed before MCP retry.");
    }
    info = await getMcpTransport(tenantId, serverId, owner);
    return fn(info.transport);
  }
}

// Call a known MCP tool by name without going through pi-ai AgentTool
// wrapping or `tools/list` discovery. Used by sidebar dim loaders that
// know exactly which tool they want (e.g. Linear's `list_issues`).
//
// Returns the raw MCP `tools/call` result: `{ content, structuredContent?,
// isError? }`. Callers are responsible for decoding the content blocks
// — Linear in particular returns a single text block whose `text` is a
// JSON string.
export async function callMcpToolDirect(tenantId, serverId, toolName, args, opts = {}) {
  return withSessionRetry(tenantId, serverId, (transport) =>
    callMcpTool(transport, toolName, args ?? {}, opts),
  );
}

// Turn a transport/handshake error into one line a user can act on.
// 401 after the registry's single refresh attempt means the
// authorization is gone; anything else keeps the server's own text.
function describeHandshakeError(err) {
  const msg = shortError(err) || "MCP connection failed";
  if (/MCP HTTP 401\b/.test(msg)) return "Authorization expired or was revoked. Reconnect to sign in again.";
  if (/MCP HTTP 403\b/.test(msg)) return "The server refused this account (HTTP 403). Check your access, then reconnect.";
  if (/MCP fetch failed/.test(msg)) return `${msg}. The server may be unreachable or may not allow browser connections.`;
  return msg;
}

// Returns the union of all AgentTools across every connected server
// for this tenant, plus one entry per server that failed to load so
// the chat can say which connector is missing instead of silently
// answering without it. One broken connector never blocks the rest.
export async function collectMcpAgentToolsReport(tenantId) {
  if (!tenantId) return { tools: [], failures: [] };
  const conns = listConnections(tenantId);
  if (!conns.length) return { tools: [], failures: [] };

  const tools = [];
  const failures = [];
  // A server that has never signed in, or whose sign-in has lapsed with
  // no refresh path, has nothing to load. Say so without a handshake:
  // an unauthenticated attempt would only produce a 401 and overwrite
  // the more specific reason the record already carries.
  const attempt = [];
  for (const conn of conns) {
    const status = connectionStatus(conn, conn.connection);
    const name = conn.name || conn.id;
    const signedIn = conn.auth === "apiKey" || !!conn.connection?.tokens?.accessToken;
    if (status === "configured") {
      failures.push({ serverId: conn.id, name, message: "Not signed in yet. Finish connecting it under Model & settings, MCP tab." });
    } else if (status === "expired") {
      failures.push({ serverId: conn.id, name, message: "Authorization expired. Reconnect under Model & settings, MCP tab." });
    } else if (status === "failed" && !signedIn) {
      // Sign-in never completed; the record already says why.
      failures.push({ serverId: conn.id, name, message: conn.connection?.lastError?.message || "Connection failed. Reconnect under Model & settings, MCP tab." });
    } else {
      attempt.push(conn);
    }
  }

  const settled = await Promise.allSettled(attempt.map((conn) => loadToolsFor(tenantId, conn)));
  for (let i = 0; i < settled.length; i++) {
    const r = settled[i];
    if (r.status === "fulfilled") {
      tools.push(...r.value);
    } else {
      console.warn(`[mcp] ${attempt[i].id} failed to load tools:`, r.reason);
      failures.push({
        serverId: attempt[i].id,
        name: attempt[i].name || attempt[i].id,
        message: describeHandshakeError(r.reason),
      });
    }
  }
  return { tools, failures };
}

export async function collectMcpAgentTools(tenantId) {
  return (await collectMcpAgentToolsReport(tenantId)).tools;
}

async function loadToolsFor(tenantId, def) {
  const owner = requireBrowserStorageOwner(tenantId);
  // tools/list itself can hit a stale session if we cold-call after a
  // long idle, so it goes through the same retry path as tools/call.
  let tools;
  try {
    tools = await withSessionRetry(
      tenantId,
      def.id,
      (transport) => listMcpTools(transport),
      owner,
    );
  } catch (err) {
    // A post-handshake failure still means "no tools from this server":
    // record it and drop the session so the picker agrees with the chat
    // and the next build starts clean.
    evictEntry(poolKey(owner, def.id));
    if (browserStorageOwnerIsCurrent(owner)) {
      recordHandshakeError(tenantId, def.id, describeHandshakeError(err));
    }
    throw err;
  }
  const call = (toolName, args, opts) =>
    withSessionRetry(
      tenantId,
      def.id,
      (transport) => callMcpTool(transport, toolName, args ?? {}, opts),
      owner,
    );
  return tools.map((t) =>
    mcpToolToAgentTool({ tool: t, serverPrefix: def.id, call }),
  );
}
